import React from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { InputActionMeta } from "react-select";

import { InputFieldProps, Overlay } from "shared/components";

import { config } from "../config";
import {
  StyledAsyncSelect,
  StyledCreatableSelect,
  StyledDropdown,
  StyledReactSelect,
} from "../styles";
import { ISelectOption } from "../types";
import { useSelectDropdownLogic } from "../utils";

export interface SelectDropdownProps extends InputFieldProps {
  options: ISelectOption[];
  control?: Control;
  selectedValue?: ISelectOption;
  Option?: React.ComponentType<any>;
  handleClose: () => void;
  handleBlur?: () => void;
  isOpen?: boolean;
  isSearchable?: boolean;
  isCreatable?: boolean;
  isMulti?: boolean;
  isFocused?: boolean;
  validation?: RegisterOptions;
  isAsync?: boolean;
  loadOptions?: (query: string) => Promise<ISelectOption[]>;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  isLoading?: boolean;
}

export const SelectDropdown: React.FC<SelectDropdownProps> = ({
  name,
  control,
  selectedValue,
  handleClose,
  isOpen,
  validation,
  isAsync,
  options,
  loadOptions,
  isLoading,
  isCreatable,
  isMulti,
  ...rest
}) => {
  const { getSelectProps } = useSelectDropdownLogic({
    ...rest,
    options,
    selectedValue,
    handleClose,
    isCreatable,
  });

  return (
    <StyledDropdown>
      {isOpen && <Overlay onClick={handleClose} />}
      <Controller
        name={name}
        control={control}
        rules={validation}
        defaultValue={selectedValue?.value || ""}
        render={({ onChange, onBlur }) => (
          <React.Fragment>
            {isOpen && (
              <>
                {isAsync ? (
                  <StyledAsyncSelect
                    {...config}
                    {...getSelectProps({ onChange, onBlur })}
                    cacheOptions
                    defaultOptions={options}
                    loadOptions={loadOptions}
                    isLoading={isLoading}
                  />
                ) : isCreatable ? (
                  <StyledCreatableSelect
                    {...config}
                    {...getSelectProps({ onChange, onBlur })}
                    isLoading={isLoading}
                    isMulti={isMulti}
                    options={options}
                  />
                ) : (
                  <StyledReactSelect
                    {...config}
                    {...getSelectProps({ onChange, onBlur })}
                    isLoading={isLoading}
                  />
                )}
              </>
            )}
          </React.Fragment>
        )}
      />
    </StyledDropdown>
  );
};
